<template>
    <Accordion class="pl-2 pr-2 space-y-2 bg-opacity-30 bg-slate-400" title="Ring">
        <property-row :key="editorKey('ring_source')" label="source">
            <select class="ring-select" :value="node.ring_source"
                @change="setProperty('ring_source', $event.target.value, 'ring_source')">
                <option v-for="source in ringSources" :key="source" :value="source">{{ source }}</option>
            </select>
        </property-row>
        <property-row :key="editorKey('ring_reference')" label="reference">
            <select class="ring-select" :value="node.ring_reference"
                @change="setProperty('ring_reference', $event.target.value, 'ring_reference')">
                <option v-for="reference in ringReferences" :key="reference" :value="reference">{{ reference }}</option>
            </select>
        </property-row>
        <property-row :key="editorKey('ring_width')" label="width">
            <form-input type="number" :no_scroll="true" min="0" :model-value="node.ring_width"
                @update:model-value="setProperty('ring_width', Number($event), 'ring_width')" />
        </property-row>
        <property-row :key="editorKey('ring_start_angle')" label="start angle">
            <form-input type="number" :no_scroll="true" min="0" max="359" :model-value="node.ring_start_angle"
                @update:model-value="setProperty('ring_start_angle', Number($event), 'ring_start_angle')" />
        </property-row>

        <template v-if="node.ring_source === 'manual'">
            <div class="flex items-center justify-between pt-2 border-t border-slate-500">
                <span class="text-sm text-white">Segments</span>
                <button class="text-white" title="Add ring segment" @click="addSegment">
                    <font-awesome-icon icon="fa-solid fa-plus-circle" />
                </button>
            </div>
        <div v-for="segment in segments" :key="editorKey(`segment:${segment.id}`)" class="p-2 space-y-1 rounded bg-slate-700/60">
            <property-row label="id/name">
                <input type="text" class="w-full min-w-0 px-1 text-gray-900 rounded border border-gray-400"
                    :value="segment.name" @input="updateSegment(segment.id, { name: $event.target.value })" />
            </property-row>
            <property-row label="value">
                <form-input type="number" :no_scroll="true" min="0" :model-value="segment.value"
                    @update:model-value="updateSegment(segment.id, { value: Number($event) })" />
            </property-row>
            <property-row label="color">
                <ColorPicker :pureColor="segment.color" useType="pure" format="hex8" lang="En" shape="circle"
                    @pureColorChange="updateSegment(segment.id, { color: $event })" />
            </property-row>
            <div class="flex items-center justify-between">
                <form-checkbox label="enabled" :model-value="segment.enabled"
                    @update:model-value="updateSegment(segment.id, { enabled: $event })" />
                <button class="px-2 text-white" title="Delete ring segment" @dblclick="deleteSegment(segment.id)">
                    <font-awesome-icon icon="fa-solid fa-trash-can" />
                </button>
            </div>
        </div>
        </template>
        <div v-else class="pt-2 space-y-1 border-t border-slate-500">
            <p class="text-xs font-semibold text-white">Current contributions</p>
            <div v-for="segment in resolvedSegments" :key="segment.id"
                class="flex items-center gap-2 px-2 py-1 text-xs rounded bg-slate-700/60">
                <span class="w-3 h-3 rounded-full" :style="{ backgroundColor: segment.color }"></span>
                <span class="flex-1 truncate text-white">{{ segment.name }}</span>
                <span class="text-slate-300">{{ segment.value }}</span>
            </div>
            <p v-if="resolvedSegments.length === 0" class="text-xs text-slate-400">No contributions</p>
        </div>
    </Accordion>
</template>

<script>
import { ColorPicker } from 'vue3-colorpicker'
import 'vue3-colorpicker/style.css'
import Accordion from '../Accordion.vue'
import PropertyRow from './PropertyRow.vue'
import { propertyEditorKey } from './propertyEditorKey.js'

export default {
    name: 'BadgePropertiesEditor',
    components: { Accordion, ColorPicker, PropertyRow },
    props: { node: { type: Object, required: true } },
    data() {
        return {
            revision: 0,
            ringSources: ['manual', 'incoming', 'outgoing', 'all'],
            ringReferences: ['badge_value', 'flow_sum'],
        }
    },
    computed: {
        segments() {
            void this.revision
            return this.node.ring_segments ?? []
        },
        resolvedSegments() {
            void this.revision
            return this.node.getResolvedRingSegments?.() ?? []
        },
    },
    mounted() {
        this.subscribeNode(this.node)
    },
    beforeUnmount() {
        this.unsubscribeNode(this.node)
    },
    watch: {
        node(nextNode, previousNode) {
            this.unsubscribeNode(previousNode)
            this.subscribeNode(nextNode)
            this.revision++
        },
    },
    methods: {
        editorKey(context) {
            return propertyEditorKey(this.node, `badge-ring:${context}`)
        },
        subscribeNode(node) {
            node?.on('ring-resolved.badge-properties', this.refreshResolved)
        },
        unsubscribeNode(node) {
            node?.off('ring-resolved.badge-properties', this.refreshResolved)
        },
        refreshResolved() {
            this.revision++
        },
        setProperty(property, value, eventName) {
            this.node[property] = value
            this.node.adjust(eventName)
            this.revision++
        },
        updateSegment(id, patch) {
            this.node.updateRingSegment(id, patch)
            this.revision++
        },
        addSegment() {
            const used = new Set(this.segments.map(segment => segment.id))
            let number = this.segments.length + 1
            while (used.has(`segment-${number}`)) number++
            this.node.setRingSegments([
                ...this.segments,
                { id: `segment-${number}`, name: `Segment ${number}`, value: 1, color: '#ffffffff', enabled: true },
            ])
            this.revision++
        },
        deleteSegment(id) {
            this.node.setRingSegments(this.segments.filter(segment => segment.id !== id))
            this.revision++
        },
    },
}
</script>

<style scoped>
.ring-select {
    width: 100%;
    padding: 0.2rem 0.3rem;
    border-radius: 0.25rem;
    background: rgb(30 41 59 / 0.7);
    color: white;
}
</style>
